import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { UpgradeModalOptions } from './types';
import { UpgradeModalContext } from './contexts';
import { useAnalytics } from 'context/analytics';
import { useAuth } from 'context/auth';

const DEFAULT_TITLE = 'This action is not supported by your plan';
const DEFAULT_SUBTITLE = 'Please upgrade to enjoy more features from Crono';

const UpgradeModalProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isUpgradeModalVisible, setIsUpgradeModalVisible] =
    useState<boolean>(false);
  const analytics = useAnalytics();
  const [customClose, setCustomClose] = useState<(() => void) | undefined>();
  const [title, setTitle] = useState<string>(DEFAULT_TITLE);
  const [subtitle, setSubtitle] = useState<string>(DEFAULT_SUBTITLE);

  const closeModal = useCallback(() => {
    if (analytics) {
      analytics.track('subscription-popup-closed-clicked', {});
    }
    if (customClose) {
      customClose();
      setCustomClose(undefined);
    }
    setTimeout(() => {
      setIsUpgradeModalVisible(false);
    }, 0);
  }, [analytics, customClose]);
  return (
    <UpgradeModalContext.Provider
      value={{
        openModal: ({ onClose, title, subtitle }: UpgradeModalOptions = {}) => {
          setIsUpgradeModalVisible(true);
          setCustomClose(() => onClose);
          setTitle(title ?? DEFAULT_TITLE);
          setSubtitle(subtitle || DEFAULT_SUBTITLE);
        },
        closeModal: closeModal,
        isUpgradeModalVisible,
        title,
        subtitle,
      }}
    >
      {children}
    </UpgradeModalContext.Provider>
  );
};

export const usePaywallGuard = (onClose?: () => void) => {
  const { closeModal } = useUpgradeModal();
  const { openPaywallIfNeeded, showUpgradeBadges } = useAuth();

  openPaywallIfNeeded(onClose);
  useEffect(() => {
    return () => {
      if (showUpgradeBadges) {
        closeModal();
      }
    };
  }, []);
};

export function useUpgradeModal() {
  const context = useContext(UpgradeModalContext);
  if (context === undefined) {
    throw new Error(
      `useUpgradeModal must be used within a UpgradeModalProvider`,
    );
  }

  return context;
}

export default UpgradeModalProvider;
